import { sumDecimals, type DashboardTodayResponse } from '@siteday/shared';

export type TodayJob = DashboardTodayResponse['jobs'][number];
export type TodayForeman = TodayJob['foremen'][number];

export interface DashboardFilters {
  foreman: string;
  client: string;
  red: boolean;
  missing: boolean;
}

/** Filters live in the URL (doc 08 W2). A job stays when at least one foreman row matches. */
export function filterDashboard(jobs: TodayJob[], f: DashboardFilters): TodayJob[] {
  return jobs
    .filter((j) => !f.client || (j.clientName ?? '') === f.client)
    .map((j) => ({
      ...j,
      foremen: j.foremen.filter((r) => {
        if (f.foreman && r.foremanId !== f.foreman) return false;
        if (f.missing && !r.missing) return false;
        if (f.red && !r.quantities.some((q) => q.band === 'red')) return false;
        return true;
      }),
    }))
    .filter((j) => j.foremen.length > 0)
    .map((j) => ({
      ...j,
      totals: {
        laborHours: sumDecimals(j.foremen.map((r) => r.laborHours)).toString(),
        machineHours: sumDecimals(j.foremen.map((r) => r.machineHours)).toString(),
        photoCount: j.foremen.reduce((a, r) => a + r.photoCount, 0),
      },
    }));
}

export interface DerivedSummary {
  foremanDays: number;
  submitted: number;
  /** Foreman-days with no finished entry that are not (yet) flagged missing, i.e. before the cutoff. */
  notYet: number;
  missing: number;
  missingNames: string[];
  codesRed: number;
  codesAmber: number;
  codesBehind: number;
  laborHours: string;
  machineHours: string;
  photoCount: number;
  photosPending: number;
  activeJobs: number;
  redJobNames: string[];
}

/** Summary strip numbers from the (possibly filtered) job rows. */
export function deriveSummary(jobs: TodayJob[]): DerivedSummary {
  const rows = jobs.flatMap((j) => j.foremen.map((r) => ({ job: j, row: r })));
  const quantities = rows.flatMap(({ row }) => row.quantities);
  const codesRed = quantities.filter((q) => q.band === 'red').length;
  const codesAmber = quantities.filter((q) => q.band === 'amber').length;
  const missingRows = rows.filter(({ row }) => row.missing);
  // "Submitted" means a finished day exists (submittedAt), not merely "not flagged missing yet".
  // Before the cutoff a foreman with no entry is neither submitted nor missing: they are "not yet".
  const submittedRows = rows.filter(({ row }) => row.submittedAt !== null);
  return {
    foremanDays: rows.length,
    submitted: submittedRows.length,
    notYet: rows.length - submittedRows.length - missingRows.length,
    missing: missingRows.length,
    missingNames: missingRows.map(
      ({ job, row }) => `${shortJob(job.jobName)} · ${row.foremanName}`,
    ),
    codesRed,
    codesAmber,
    codesBehind: codesRed + codesAmber,
    laborHours: sumDecimals(rows.map(({ row }) => row.laborHours)).toString(),
    machineHours: sumDecimals(rows.map(({ row }) => row.machineHours)).toString(),
    photoCount: rows.reduce((a, { row }) => a + row.photoCount, 0),
    photosPending: rows.reduce((a, { row }) => a + row.photosPending, 0),
    activeJobs: jobs.length,
    redJobNames: [
      ...new Set(
        rows
          .filter(({ row }) => row.quantities.some((q) => q.band === 'red'))
          .map(({ job }) => shortJob(job.jobName)),
      ),
    ],
  };
}

/** "Oak Ridge Phase 2 — Storm sewer" -> "Oak Ridge Phase 2" for tight spaces. */
export function shortJob(name: string): string {
  return name.split(' — ')[0] ?? name;
}

/** Distinct foremen and clients for the filter selects. */
export function filterOptions(jobs: TodayJob[]) {
  const foremen = new Map<string, string>();
  const clients = new Set<string>();
  for (const j of jobs) {
    if (j.clientName) clients.add(j.clientName);
    for (const r of j.foremen) foremen.set(r.foremanId, r.foremanName);
  }
  return {
    foremen: [...foremen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    clients: [...clients].sort(),
  };
}

/** Quantity totals for a multi-foreman job: "210 LF · 2 EA". */
export function jobQuantityTotals(job: TodayJob, format: (v: string) => string): string {
  const byUnit = new Map<string, string[]>();
  for (const r of job.foremen)
    for (const q of r.quantities)
      if (q.actual !== null) byUnit.set(q.unit, [...(byUnit.get(q.unit) ?? []), q.actual]);
  const parts = [...byUnit.entries()].map(
    ([unit, xs]) => `${format(sumDecimals(xs).toString())} ${unit}`,
  );
  return parts.length ? parts.join(' · ') : '—';
}
